import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";

const font = { fontFamily: "Poppins" };

const HeaderFilter = ({
  label,
  align = "left",
  type = "multi",
  options = [],
  value,
  onApply,
  active,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const [draftText, setDraftText] = useState("");
  const [draftValues, setDraftValues] = useState([]);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setDraftText(type === "search" ? value || "" : "");
    setDraftValues(type === "multi" ? [...(value || [])] : []);
  }, [open, type, value]);

  const visibleOptions = options.filter((option) =>
    String(option).toLowerCase().includes(search.trim().toLowerCase()),
  );

  const toggleValue = (option) => {
    setDraftValues((current) =>
      current.includes(option)
        ? current.filter((item) => item !== option)
        : [...current, option],
    );
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        gap: 0.25,
        width: "100%",
      }}
    >
      <Typography component="span" sx={{ ...font, fontWeight: 600, fontSize: 13 }}>
        {label}
      </Typography>
      <IconButton
        size="small"
        aria-label={`Filter ${label}`}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          p: 0.25,
          color: active ? "primary.main" : "text.secondary",
        }}
      >
        <FilterListIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, width: 260 }}>
          {type === "search" ? (
            <TextField
              autoFocus
              size="small"
              fullWidth
              label="Search"
              value={draftText}
              onChange={(event) => setDraftText(event.target.value)}
            />
          ) : (
            <>
              <TextField
                autoFocus
                size="small"
                fullWidth
                label="Search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ mb: 1 }}
              />
              <Box sx={{ maxHeight: 220, overflowY: "auto" }}>
                {visibleOptions.length ? (
                  visibleOptions.map((option) => (
                    <FormControlLabel
                      key={option}
                      sx={{ display: "flex", ml: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={draftValues.includes(option)}
                          onChange={() => toggleValue(option)}
                        />
                      }
                      label={
                        <Typography sx={{ ...font, fontSize: 13 }}>
                          {option}
                        </Typography>
                      }
                    />
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No matching values
                  </Typography>
                )}
              </Box>
            </>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
            <Button
              size="small"
              onClick={() => {
                if (type === "search") {
                  setDraftText("");
                  onApply("");
                } else {
                  setDraftValues([]);
                  onApply([]);
                }
                setAnchorEl(null);
              }}
            >
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                onApply(type === "search" ? draftText : draftValues);
                setAnchorEl(null);
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default HeaderFilter;
